import type { ErrorLike } from '@apollo/client';
import type {
  DefaultOrganizationQuery,
  OrganizationFragment,
  SimpleOrganizationByIdQuery
} from '../__generated__/apollo-hooks';
import { GLOBAL_ORGANIZATION_ID } from './organizations.helper';

type QueryState<TData> = {
  data?: TData;
  previousData?: TData;
  loading: boolean;
  error?: ErrorLike;
};

/**
 * - `resolving`: no organization is known yet.
 * - `switching`: one is on screen, but the selection is moving to another.
 * - `ready`: the selected organization is resolved.
 * - `missing`: the server returned nothing for the selected id.
 * - `failed`: the lookup errored, so whether it exists is unknown.
 */
export type OrganizationStatus =
  | 'resolving'
  | 'switching'
  | 'ready'
  | 'missing'
  | 'failed';

export type SelectedOrganizationState = {
  status: OrganizationStatus;
  selectedOrganization: OrganizationFragment | undefined;
  selectedOrganizationId: string | undefined;
  loading: boolean;
  isMissingOrg: boolean;
  error?: ErrorLike;
  lastKnownOrgName?: string;
};

type DeriveSelectedOrganizationInput = {
  selectedOrganizationId: string | undefined;
  /** A deep-linked organization is resolving and will replace the selection. */
  deepLinkPending: boolean;
  byId: QueryState<SimpleOrganizationByIdQuery>;
  defaultOrganization: QueryState<DefaultOrganizationQuery>;
};

export const deriveSelectedOrganization = ({
  selectedOrganizationId,
  deepLinkPending,
  byId,
  defaultOrganization
}: DeriveSelectedOrganizationInput): SelectedOrganizationState => {
  const defaultNode =
    defaultOrganization.data?.organizations?.edges?.at(0)?.node;
  // The default result stands in only until its id is selected and `byId`
  // takes over; after a switch it would be a different org.
  const fromDefault =
    defaultNode &&
    (!selectedOrganizationId || defaultNode.id === selectedOrganizationId)
      ? defaultNode
      : undefined;
  const current = byId.data?.organization ?? fromDefault;
  const previous = byId.previousData?.organization;

  const query = selectedOrganizationId ? byId : defaultOrganization;
  const settled =
    !query.loading && (query.data !== undefined || Boolean(query.error));
  const failed = settled && Boolean(query.error);

  // Keep the previous org on screen while the next one loads or a lookup
  // fails; drop it only once the server says the selected org is gone.
  const selectedOrganization =
    current ?? (settled && !failed ? undefined : previous);

  const status = ((): OrganizationStatus => {
    if (deepLinkPending) {
      return 'switching';
    }
    if (current) {
      return 'ready';
    }
    if (!settled) {
      return selectedOrganization ? 'switching' : 'resolving';
    }
    if (failed) {
      return 'failed';
    }
    return selectedOrganizationId &&
      selectedOrganizationId !== GLOBAL_ORGANIZATION_ID
      ? 'missing'
      : 'resolving';
  })();

  return {
    status,
    selectedOrganization,
    selectedOrganizationId,
    loading: deepLinkPending || byId.loading || defaultOrganization.loading,
    // A failure with nothing on screen still needs the recovery modal; without
    // an org, the selector would otherwise read as Global.
    isMissingOrg:
      status === 'missing' || (status === 'failed' && !selectedOrganization),
    error: byId.error ?? defaultOrganization.error,
    lastKnownOrgName: (current ?? previous)?.name
  };
};
